import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ProgramsStateInitialState } from './programs.state';
import { v4 as uuidv4 } from 'uuid';

const initialState = ProgramsStateInitialState;

export const programsSlice = createSlice({
  name: 'programs',
  initialState,
  reducers: {
    ADD_COLUMNS: (state, action) => {
      state.programColumns = { ...state.programColumns, ...action.payload };
    },
    REMOVE_COLUMN: (state, action: PayloadAction<string>) => {
      const columnId = action.payload;
      if (columnId === 'pinnedColumn') return;

      let foundProgId: string;
      let willRemoveColumn = true;

      state.programColumns[columnId].map((programId) => {
        if (programId !== 'widget') {
          willRemoveColumn = false;
          foundProgId = programId;
        }
      });
      if (willRemoveColumn) {
        // Use Immer's direct mutation instead of Object.fromEntries pattern
        delete state.programColumns[columnId];
      } else {
        state.programColumns[columnId] = [foundProgId];
      }
    },
    SPLIT_COLUMN: (state, action: PayloadAction<string>) => {
      const selectedColumn = state.programColumns[action.payload];
      if (selectedColumn.length > 1) return;
      const newWidgetId = `widget`;
      const newColumnValue = [...selectedColumn, newWidgetId];
      state.programColumns = { ...state.programColumns, [action.payload]: newColumnValue };
    },
    FULL_COLUMN: (state, action: PayloadAction<string>) => {
      const selectedColumn = [...state.programColumns[action.payload]];
      if (selectedColumn.length === 1) return;

      const [firstProgram, secondProgram] = selectedColumn;
      if (firstProgram === 'widget') {
        state.programColumns[action.payload] = [secondProgram];
      } else {
        state.programColumns[action.payload] = [firstProgram];
        if (secondProgram === 'widget') return;

        const newColumnId = `column${uuidv4()}`;
        state.programColumns = {
          ...state.programColumns,
          [newColumnId]: [secondProgram],
        };
      }
    },
    PIN_COLUMN: (state, action: PayloadAction<string>) => {
      const selectedColumn = state.programColumns[action.payload];
      const currentPinnedColumn = state.programColumns['pinnedColumn'];
      if (currentPinnedColumn.some((value) => value !== 'widget')) {
        state.programColumns[action.payload] = currentPinnedColumn;
      } else {
        // Use Immer's direct mutation instead of Object.fromEntries pattern
        delete state.programColumns[action.payload];
      }
      state.programColumns['pinnedColumn'] = selectedColumn;
    },
    ADD_PROGRAM: (
      state,
      action: PayloadAction<{ columnId: string; widgetIndex: number; programId: string }>
    ) => {
      const { columnId, widgetIndex, programId } = action.payload;
      if (!state.programColumns[columnId]) {
        state.programColumns[columnId] = [programId];
      } else {
        const selectedColumn = state.programColumns[columnId];
        selectedColumn[widgetIndex] = action.payload.programId;
      }
    },
    REMOVE_PROGRAM: (state, action: PayloadAction<string>) => {
      const programId = action.payload;
      //find the column and index of the program
      let columnId = '';
      let index = 0;
      let willRemoveColumn = true;
      for (const [key, value] of Object.entries(state.programColumns)) {
        const foundIndex = value.indexOf(programId);
        const otherValues = value.filter((v) => v !== programId);
        if (foundIndex >= 0) {
          columnId = key;
          index = foundIndex;
          if (
            (otherValues.length > 0 && !otherValues.includes('widget')) ||
            columnId === 'pinnedColumn'
          ) {
            willRemoveColumn = false;
          }
          break;
        }
      }
      //if found, replace the program with 'widget'
      if (columnId) {
        if (!willRemoveColumn) {
          state.programColumns[columnId][index] = 'widget';
        } else {
          // Use Immer's direct mutation instead of Object.fromEntries pattern
          delete state.programColumns[columnId];
        }
      }
    },
    UPDATE_PROGRAMS: (state, action: PayloadAction<(string | number)[]>) => {
      const programIds = action.payload.map(String);
      const updatedColumns: { [key: string]: string[] } = {};
      const existingProgramIds = new Set<string>();
      const placeholders: { columnId: string; index: number }[] = [];

      if (programIds.length === 0) {
        state.programColumns = { pinnedColumn: ['widget'] };
        return;
      }

      const currentPinned = state.programColumns.pinnedColumn?.[0];
      let pinnedId: string | undefined;

      // Determine pinnedId
      if (currentPinned && programIds.includes(currentPinned) && currentPinned !== 'widget') {
        pinnedId = currentPinned;
      } else {
        pinnedId = programIds[0];
      }

      updatedColumns['pinnedColumn'] = pinnedId ? [pinnedId] : [];
      existingProgramIds.add(pinnedId);

      const restIds = programIds.filter((id) => id !== pinnedId);

      // Step 1: Copy over existing columns, except pinned
      for (const [columnId, programs] of Object.entries(state.programColumns)) {
        if (columnId === 'pinnedColumn') continue;

        updatedColumns[columnId] = [];

        for (let i = 0; i < programs.length; i++) {
          const id = programs[i];
          if (restIds.includes(id)) {
            updatedColumns[columnId].push(id);
            existingProgramIds.add(id);
          } else {
            updatedColumns[columnId].push('widget');
            placeholders.push({ columnId, index: i });
          }
        }
      }

      // Step 2: Add remaining new IDs into placeholders or new columns
      let payloadIndex = 0;
      for (const id of restIds) {
        if (existingProgramIds.has(id)) continue;

        if (payloadIndex < placeholders.length) {
          const { columnId, index } = placeholders[payloadIndex++];
          updatedColumns[columnId][index] = id;
        } else {
          const newColumnId = `column-${uuidv4()}`;
          updatedColumns[newColumnId] = [id];
        }
      }

      const nonPinnedColumnIds = Object.keys(updatedColumns).filter((id) => id !== 'pinnedColumn');

      for (let i = 0; i < nonPinnedColumnIds.length; i++) {
        const columnId = nonPinnedColumnIds[i];
        const isLastColumn = i === nonPinnedColumnIds.length - 1;
        const programs = updatedColumns[columnId];

        if (isLastColumn) {
          const widgetCount = programs.filter((id) => id === 'widget').length;
          if (widgetCount === programs.length) {
            // All widgets – keep only one
            updatedColumns[columnId] = ['widget'];
          } else {
            // Keep non-widget values, remove all widgets
            updatedColumns[columnId] = programs.filter((id) => id !== 'widget');
          }
        } else {
          // Remove all widgets
          updatedColumns[columnId] = programs.filter((id) => id !== 'widget');
          if (updatedColumns[columnId].length === 0) {
            delete updatedColumns[columnId];
          }
        }
      }
      state.programColumns = { ...updatedColumns };
    },
    UPDATE_PROGRAMS_SETTINGS: (state, action: PayloadAction<IGridColumns>) => {
      state.programColumns = {
        ...state.programColumns,
        ...action.payload,
      };
      state.hasInitialized = true;
    },
  },
});

export default programsSlice.reducer;
